/**
 * Leads: an email left for a checklist or guide, and the one follow-up that comes a day later (docs/LEADS.md).
 *
 * Rules (owner, 2026-09-30):
 * - Gate planning content, never safety. A planning checklist or guide (the Utah permit checklists, the how-long
 *   checklists, the opening and inspection-prep guides) asks for an email first. Anything someone may need in the middle
 *   of a bad moment (a sick worker, a vomit or diarrhea cleanup, a cut or burn, chemicals, a recall, a fire, a water
 *   outage, a return to work) is a direct download, always. The two lists are explicit below, and a document nobody
 *   classified is OPEN (a mistake here must never put a wall in front of safety). The tests check both lists against
 *   what the landing build makes.
 * - The follow-up goes only to a person who ticked the unchecked consent box. CAN-SPAM (16 CFR 316) allows commercial
 *   email to people who did not opt in as long as it says so, has a working opt-out and a postal address; asking first
 *   is kinder, and the owner's rule is review before anything reaches a real person.
 * - 10% off the first month: Stripe charges nothing for the first 25 days, so the discount is a coupon that covers one
 *   month from the day the plan starts (duration repeating, 1 month). The 25 days are shorter than any month, so the first
 *   paid charge falls inside it and the second does not (leadDiscountCoversInvoice below, with its test).
 * - No price is written here: the number in the email is the lowest band in billing.ts.
 */
import { COMPANY_POSTAL_LINE } from "./company.ts";
import { PLAN_BANDS, TRIAL_DAYS } from "./billing.ts";
import { isAppSrc } from "./pdfad.ts";
import { LIBRARY } from "./library.ts";
import { LEADS_UT } from "./states/ut/leads.ts";
import { loadedStates, perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";
import { isSupportedState } from "./states/types.ts";
import { STATE_NAMES } from "./states/fda-adoption.ts";
import { packFor } from "./states/index.ts";
import { caPlaceName, caStartFile, nyPlaceName, nyStartFile, orPlaceName, orStartFile } from "./startslug.ts";

const WORDS: Record<UsState, StateWords<typeof LEADS_UT>> = perStateTable("LEADS", LEADS_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/* ───────────────────────── business types ───────────────────────── */

export const LEAD_BUSINESS_TYPES = [
  { id: "coffee_shop", label: "Coffee shop", noun: "coffee shop" },
  { id: "restaurant", label: "Restaurant", noun: "restaurant" },
  { id: "food_truck", label: "Food truck", noun: "food truck" },
  { id: "event_booth", label: "Event booth", noun: "event booth" },
  { id: "home", label: "Home kitchen", noun: "home kitchen" },
  { id: "other", label: "Something else", noun: "food business" },
] as const;
export type LeadBusinessType = (typeof LEAD_BUSINESS_TYPES)[number]["id"];
export const isLeadBusinessType = (v: unknown): v is LeadBusinessType => LEAD_BUSINESS_TYPES.some((t) => t.id === v);
export const businessNoun = (t: string | null | undefined): string => LEAD_BUSINESS_TYPES.find((b) => b.id === t)?.noun ?? "food business";

/* ───────────────────────── consent ───────────────────────── */

/** The words next to the box. Change the words, change the version: every lead keeps the version it saw. */
export const LEAD_CONSENT_VERSION = "2026-09-30.1";
export const LEAD_CONSENT_TEXT = "Send me CleanCleaver food-safety tips and offers (unsubscribe anytime)";

/* ───────────────────────── the documents ───────────────────────── */

export type LeadDocKind = "permit" | "howlong" | "article" | "guide" | "template";
export interface LeadDoc {
  /** The PDF's file name under /checklists/, or `guide:<id>` for a library guide read on the site. */
  file: string;
  kind: LeadDocKind;
  /** Words for "Your ___" in the subject and the first line. */
  title: string;
  /** One useful tip tied to this document. A food-safety number carries its Food Code section. */
  tip: string;
}

const permit = (file: string, title: string, tip: string): LeadDoc => ({ file, kind: "permit", title, tip });
const howlong = (slug: string, title: string, tip: string): LeadDoc => ({ file: `how-long-${slug}.pdf`, kind: "howlong", title, tip });
const article = (slug: string, title: string, tip: string): LeadDoc => ({ file: `${slug}.pdf`, kind: "article", title, tip });

const coldTip = (state?: UsState): string => {
  const adopt = words(state).coldAdoption;
  return `Put a thermometer in each fridge today, and read it before you open. Cold food you hold for later has to stay at 41°F or colder (Food Code 3-501.16${adopt ? `, ${adopt}` : ""}).`;
};
const WALK = "Walk your place with the list and a pen before an inspector does. Mark each line ready or not yet, and fix the not yet lines first.";

/**
 * PLANNING documents: these ask for an email. The how-long tips are the first step of each food's own page, word for word
 * (apps/landing checks that they still match).
 */
/** The tip a Utah-pack row names ("$COLD", "$WALK") or writes out. */
const tipOf = (tip: string, state?: UsState): string => (tip === "$COLD" ? coldTip(state) : tip === "$WALK" ? WALK : tip);

/** No state given (the server, which sees only a file name): every supported state's documents, each file once. */
const allStates = <T extends { file: string }>(f: (s: UsState) => readonly T[]): readonly T[] => [...new Map(loadedStates().flatMap(f).map((d) => [d.file, d])).values()];

/** California's "Start a food business in <County>" checklists (one per place, apps/landing pages-src/site/california-start.ts): planning, so they ask for an email. */
/** New York's "Start a food business in <County>" checklists (62 places, apps/landing pages-src/site/new-york-start.ts): same gate. */
const nyStartDocs = (): LeadDoc[] => packFor("NY").counties.map((p) => permit(nyStartFile(p), `${nyPlaceName(p)} start-up checklist`, "Call the county's health office before you spend money on a build-out. The list names who to call and what to bring."));
/** Oregon's "Start a food business in <County>" checklists (36 counties): same gate. */
const orStartDocs = (): LeadDoc[] => packFor("OR").counties.map((p) => permit(orStartFile(p), `${orPlaceName(p)} start-up checklist`, "Call the county's environmental health office before you spend money on a build-out. The list names who to call and what to bring."));
const caStartDocs = (): LeadDoc[] => packFor("CA").counties.map((p) => permit(caStartFile(p), `${caPlaceName(p)} start-up checklist`, "Call the county's environmental health office before you spend money on a build-out. The list names who to call and what to bring."));

export function gatedDocsFor(state?: UsState): readonly LeadDoc[] {
  if (state === undefined) return allStates(gatedDocsFor);
  const w = words(state);
  return [
    ...(w.permitDocs ?? []).map((d) => permit(d.file, d.title, tipOf(d.tip, state))),
    howlong("pastries", "pastry case checklist", "Check the cold case. Put a thermometer in the cold case before you fill it. It should read 41°F or colder."),
    howlong("cheesecake", "cheesecake checklist", "Cool it on the clock. From the oven, get it to 70°F within 2 hours and to 41°F within 6 hours in all. Write the times down."),
    howlong("cold-brew", "cold brew checklist", "Steep it in the fridge. Steep in the fridge, not on the counter, so the question never comes up."),
    howlong("milk", "milk checklist", "Write on the jug when you open it. Write the day opened and the pour-out day. Opened Monday, poured out after Sunday."),
    howlong("half-and-half", "cream and half-and-half checklist", "Date the carton when you open it. Write the day opened and the pour-out day on it."),
    howlong("whipped-cream", "whipped cream checklist", "Make it from cold cream. Use cream straight from a fridge at 41°F or colder."),
    howlong("eggs", "egg checklist", "Refrigerate eggs on arrival. Straight into a fridge that holds 45°F or colder, not the prep shelf."),
    howlong("hard-boiled-eggs", "hard-boiled egg checklist", "Cool them in the fridge. If you cool them in water or peel them, they are TCS: into the fridge, 41°F or colder."),
    howlong("yogurt", "yogurt and parfait checklist", "Keep tubs cold. 41°F or colder, lids on."),
    howlong("cut-fruit", "cut fruit checklist", "Wash whole fruit before cutting. Wash the rind too: the knife carries what's on it into the fruit."),
    howlong("sandwiches", "grab-and-go sandwich checklist", "Check the fillings' dates. Find the oldest date among the fillings."),
    howlong("deli-meat", "deli meat checklist", "Date it the day you open it. Write the day opened and the throw-out day on the package or container."),
    howlong("cooked-rice", "cooked rice checklist", "Spread it out to cool. Shallow pans, a thin layer, uncovered until cold."),
    howlong("soup", "soup checklist", "Check the soup well. Take its temperature. It should read 135°F or hotter."),
    article("coffee-shop-opening-closing-checklist", "coffee shop open and close checklist", "Open with the same four checks every day: thermometer, fridge, sanitizer and restroom. Put the list where the first person in sees it."),
    // SEO round 3 (2026-10-11): checklists and guides, not in-the-moment safety steps, so gated with the app pitch (owner).
    article("restaurant-opening-closing-checklist", "restaurant open and close checklist", "Start every shift with the health check-in, then the thermometer, the fridges and the sanitizer."),
    article("restaurant-staff-food-safety-training", "staff training checklist", "Teach one short lesson a day at the start of the shift, at the station where it's used."),
    article("walk-in-cooler-temperature", "walk-in cooler checklist", "Read the thermometer in the warmest part of the walk-in: 41°F or colder."),
    article("ice-machine-cleaning-restaurant", "ice machine cleaning checklist", "Wash the ice scoop every day and keep it outside the ice."),
    article("flat-top-griddle-cleaning", "griddle cleaning checklist", "Clean the griddle warm, not cold: let it cool to 275 to 300°F, then water and the scraper."),
    article("fryer-oil-when-to-change", "fryer oil checklist", "Change oil that smokes or foams when cooking, and filter it at least daily."),
    article("food-safety-app-comparison", "questions to ask before you pick an app", "Write down each tool's price and the date you checked it."),
    article("how-often-clean-fridge", "fridge cleaning checklist", "Once a week, throw out food that's past its time."),
    article("how-long-are-leftovers-good-for", "leftovers card", "Eat leftovers within 3 to 4 days, or freeze them."),
    ...(w.articleDocs ?? []).map((d) => article(d.file.replace(/\.pdf$/, ""), d.title, tipOf(d.tip, state))),
    ...(state === "CA" ? caStartDocs() : []),
    ...(state === "NY" ? nyStartDocs() : []),
    ...(state === "OR" ? orStartDocs() : []),
  ];
}
export const GATED_DOCS: readonly LeadDoc[] = gatedDocsFor("UT");


/**
 * SAFETY documents: always a direct download, no email, no form in the way. Someone may be in the middle of it. The
 * library's always-free guides (apps/landing/build-library.ts ALWAYS_FREE) are guides, not PDFs; the two PDFs that
 * cover the same ground are listed here, and so is every in-the-moment procedure we make a PDF for.
 */
export function openDocsFor(state?: UsState): readonly Pick<LeadDoc, "file" | "kind" | "title">[] {
  if (state === undefined) return allStates(openDocsFor);
  const w = words(state);
  return [
    ...(w.sickDoc ? [w.sickDoc] : []),
    { file: "vomit-cleanup-procedure-restaurant.pdf", kind: "article", title: "vomit cleanup checklist" },
    { file: "cooling-cooked-food-safely-two-stage.pdf", kind: "article", title: "cooling checklist" },
    { file: "date-marking-7-day-rule-ready-to-eat.pdf", kind: "article", title: "date marking checklist" },
    { file: "sanitizer-test-strips-ppm-quat-chlorine.pdf", kind: "article", title: "sanitizer checklist" },
    { file: "espresso-machine-grinder-cleaning.pdf", kind: "article", title: "espresso machine cleaning checklist" },
    // SEO round 3 (2026-10-11): trouble moments. Someone may be in the middle of it, so never behind a form.
    { file: "power-outage-restaurant-food-safety.pdf", kind: "article", title: "power outage checklist" },
    { file: "boil-water-advisory-restaurant.pdf", kind: "article", title: "boil water checklist" },
    { file: "customer-got-sick-after-eating-restaurant.pdf", kind: "article", title: "customer got sick checklist" },
    { file: "mouse-in-restaurant-what-to-do.pdf", kind: "article", title: "mouse checklist" },
    { file: "fruit-flies-restaurant-bar.pdf", kind: "article", title: "fruit fly checklist" },
    ...(w.pestDoc ? [w.pestDoc] : []),
    ...(w.openArticleDocs ?? []),
    // The free templates (apps/landing /templates/): logs and checklists a team keeps every day, never behind an email.
    ...TEMPLATE_DOCS,
  ];
}
/** The free template sheets (apps/landing pages-src/templates): open, like the safety PDFs. lead-gate.test.ts checks this list against the build. */
export const TEMPLATE_DOCS: readonly Pick<LeadDoc, "file" | "kind" | "title">[] = [
  { file: "restaurant-cleaning-checklist.pdf", kind: "template", title: "restaurant cleaning checklist" },
  { file: "daily-kitchen-cleaning-checklist.pdf", kind: "template", title: "daily kitchen cleaning checklist" },
  { file: "commercial-kitchen-deep-cleaning-checklist.pdf", kind: "template", title: "deep cleaning checklist" },
  { file: "restaurant-temperature-log.pdf", kind: "template", title: "temperature log" },
  { file: "refrigerator-freezer-temperature-log.pdf", kind: "template", title: "fridge and freezer log" },
  { file: "food-cooling-log.pdf", kind: "template", title: "cooling log" },
  { file: "sanitizer-log.pdf", kind: "template", title: "sanitizer log" },
  { file: "haccp-plan-template.pdf", kind: "template", title: "HACCP plan worksheet" },
  { file: "employee-illness-policy.pdf", kind: "template", title: "employee illness policy" },
  { file: "restaurant-self-inspection-checklist.pdf", kind: "template", title: "self-inspection checklist" },
];
export const OPEN_DOCS: readonly Pick<LeadDoc, "file" | "kind" | "title">[] = openDocsFor("UT");


/**
 * Words in a file name that mean a safety moment. A file with one of these in its name is OPEN even if somebody put it on
 * the gated list by mistake. (Matched on whole dash-separated words, so cut-fruit is not a cut or burn.)
 */
export const SAFETY_WORD = /(^|[-_.])(sick|vomit|diarrhea|recall|injury|injuries|fire|outage|water-out|burn|burns|chemical|chemicals|poison|emergency|norovirus|illness|return-to-work)([-_.]|$)/i;

const byFile = (state?: UsState) => new Map<string, LeadDoc>(gatedDocsFor(state).map((d) => [d.file, d]));
const openFiles = (state?: UsState) => new Set(openDocsFor(state).map((d) => d.file));

/* The library's guides (read on the page, after the free reads: apps/landing library-src/meter.js). A guide is `guide:<id>`. */
export const GUIDE_PREFIX = "guide:";
/** Guides nobody should ever be blocked from: someone may be in the middle of it. (The library's own list; the landing build reads it from here.) */
export const LIBRARY_ALWAYS_FREE: ReadonlySet<string> = new Set(["vomit-diarrhea-cleanup", "sick-workers", "cuts-burns-bandages", "chemicals-sds", "return-to-work"]);
/** Always free to read, never counted, never gated: the list above, and any guide whose id has a safety word (a recall, a fire, a water outage). */
export const isLibraryGuideFree = (id: string): boolean => LIBRARY_ALWAYS_FREE.has(id) || SAFETY_WORD.test(id);
const guideIdOf = (file: string): string | null => (file.startsWith(GUIDE_PREFIX) ? file.slice(GUIDE_PREFIX.length) : null);
const GUIDES = new Map(LIBRARY.map((t) => [t.id, t]));
/** The gated guides' ids: every guide that is not on the free list. */
export const gatedGuideIds = (): string[] => LIBRARY.filter((t) => !isLibraryGuideFree(t.id)).map((t) => t.id);

/**
 * "Tell me when my state is ready" (docs/STATES.md): `waitlist:<two-letter state>` for a state we don't cover yet. It keeps the
 * email (the leads table, consent as ticked) and sends nothing now: no checklist, no discount email. The owner writes when the
 * state's pack is ready.
 */
export const WAITLIST_PREFIX = "waitlist:";
export const isWaitlistDoc = (file: string): boolean => {
  const m = /^waitlist:([a-z]{2})$/.exec(file);
  return !!m && !!STATE_NAMES[m[1]!.toUpperCase()] && !isSupportedState(m[1]!.toUpperCase());
};

/**
 * "Send me the app" on cleancleaver.com/get/ (the page a PDF's QR opens): `app:pdf-<document type>` (or `app:direct`). It keeps the email (the leads
 * table, consent as ticked, which is unchecked unless the person checks it) and sends no checklist and no discount email: the page
 * itself goes straight on to sign-up. The source is a document type only (pdfad.ts).
 */
export const APP_PREFIX = "app:";
export const isAppDoc = (file: string): boolean => file.startsWith(APP_PREFIX) && isAppSrc(file.slice(APP_PREFIX.length));

/** True only for a document on the gated list (a PDF) or a library guide not on the free list, whose name is not a safety word. Unknown files are open. */
export function isGatedDoc(file: string, state?: UsState): boolean {
  if (isWaitlistDoc(file) || isAppDoc(file)) return true;
  const g = guideIdOf(file);
  if (g !== null) return GUIDES.has(g) && !isLibraryGuideFree(g);
  return byFile(state).has(file) && !SAFETY_WORD.test(file);
}
/** Every document we know by name, gated or open. */
export const isKnownDoc = (file: string, state?: UsState): boolean => { if (isWaitlistDoc(file) || isAppDoc(file)) return true; const g = guideIdOf(file); return g !== null ? GUIDES.has(g) : byFile(state).has(file) || openFiles(state).has(file); };
/** The gated PDFs (not the guides). */
export const gatedFiles = (state?: UsState): string[] => gatedDocsFor(state).filter((d) => isGatedDoc(d.file, state)).map((d) => d.file);
export function leadDoc(file: string, state?: UsState): LeadDoc | undefined {
  if (isWaitlistDoc(file) || isAppDoc(file) || !isGatedDoc(file, state)) return undefined; // a waitlist or app sign-up gets no follow-up email

  const g = guideIdOf(file);
  if (g !== null) { const t = GUIDES.get(g)!; return { file, kind: "guide", title: t.title, tip: t.summary }; }
  return byFile(state).get(file);
}
/** Where the document is on the site. */
export const leadDocPath = (file: string): string => { if (isWaitlistDoc(file)) return "/states/"; if (isAppDoc(file)) return "/get/"; const g = guideIdOf(file); return g !== null ? `/library/${g}/` : `/checklists/${file}`; };
/** The document's name for the subject and the first line. A permit checklist takes the person's own kind of place when it fits. */
export function leadDocTitle(file: string, businessType?: string | null, state?: UsState): string {
  const d = leadDoc(file, state);
  if (!d) return "checklist";
  const own = words(state).ownPlaceDoc;
  if (own && file === own.file && (businessType === "coffee_shop" || businessType === "restaurant")) return `${own.titlePrefix} ${businessNoun(businessType)} checklist`;
  return d.title;
}

/* ───────────────────────── the code and the discount ───────────────────────── */

export const LEAD_DISCOUNT_PERCENT = 10;
export const LEAD_DISCOUNT_MONTHS = 1;
/** The follow-up goes about a day after the download. */
export const LEAD_FOLLOWUP_HOURS = 24;
/** A code works for 90 days from the day the person ticked the box. */
export const LEAD_CODE_VALID_DAYS = 90;
/** No 0, 1, O, I or L: a code read off a phone and typed by hand. */
const CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
export const LEAD_CODE_RE = /^CC10-[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{8}$/;

/** `random(n)` returns n random bytes (crypto.getRandomValues in the server). */
export function makeLeadCode(random: (n: number) => Uint8Array): string {
  const bytes = random(8);
  let s = "";
  for (const b of bytes) s += CODE_ALPHABET[b % CODE_ALPHABET.length];
  return `CC10-${s}`;
}
export const normalizeLeadCode = (v: unknown): string | null => {
  const s = typeof v === "string" ? v.trim().toUpperCase() : "";
  return LEAD_CODE_RE.test(s) ? s : null;
};

/** The coupon the owner makes in the Stripe Dashboard (docs/LEADS.md): 10% off, for one month from the day the plan starts. */
export const LEAD_COUPON_SPEC = { percent_off: LEAD_DISCOUNT_PERCENT, duration: "repeating", duration_in_months: LEAD_DISCOUNT_MONTHS, name: "CleanCleaver: 10% off your first month" } as const;
/** The shortest calendar month (February). A Stripe month is a calendar month, so the trial must be shorter than this. */
const SHORTEST_MONTH_DAYS = 28;

/**
 * Does the coupon take 10% off this charge? `paidChargeNumber` counts the charges that are not zero: 1 is the first paid
 * month (day `trialDays` after the plan starts), 2 is the next one. The coupon covers the first calendar month from the day
 * the plan starts, so the first paid charge is inside it (the trial is shorter than any month) and the second is not.
 */
export function leadDiscountCoversInvoice(paidChargeNumber: number, trialDays: number = TRIAL_DAYS): boolean {
  if (paidChargeNumber < 1) return false;
  if (paidChargeNumber === 1) return trialDays < SHORTEST_MONTH_DAYS;
  return false;
}
/** What the first paid month costs with the code, from the cents billing.ts gives. Rounds to the cent. */
export function discountedFirstMonthCents(monthlyCents: number): number {
  return monthlyCents - Math.round((monthlyCents * LEAD_DISCOUNT_PERCENT) / 100);
}

/* ───────────────────────── the email address ───────────────────────── */

export const normalizeEmail = (v: unknown): string => (typeof v === "string" ? v.trim().toLowerCase() : "");
/** One address, a dot in the domain, no spaces or control characters, no more than 254 characters. Not a deliverability check. */
export function validLeadEmail(v: string): boolean {
  return v.length <= 254 && /^[^\s@<>(),;:"\\[\]]+@[^\s@<>(),;:"\\[\]]+\.[a-z]{2,}$/i.test(v) && !/\.\./.test(v);
}

/* ───────────────────────── the follow-up email ───────────────────────── */

/** CAN-SPAM needs a real postal address: the company's, confirmed by the owner 2026-10-08 (company.ts). */
export const LEAD_POSTAL_ADDRESS = COMPANY_POSTAL_LINE;

export interface LeadFollowupInput {
  businessType: string;
  /** The PDF's file name. */
  file: string;
  /** Where the PDF is, in full. */
  docUrl: string;
  /** Sign-up link that carries the code. */
  signupUrl: string;
  code: string;
  /** ISO time the person ticked the box. */
  consentAt: string;
  /** ISO time the code stops working. */
  codeEndsAt: string;
  unsubscribeUrl: string;
  postalAddress?: string;
}
export interface LeadFollowupEmail { subject: string; text: string; html: string; headers: { name: string; value: string }[] }

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** "December 29, 2026" from an ISO time, in UTC (a calendar date for a line of text). */
export const leadDate = (iso: string): string => { const d = new Date(iso); return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`; };
const escHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** The lowest monthly price in billing.ts, in dollars ("$8"). */
const fromPrice = (): string => { const c = PLAN_BANDS[0]!.priceCents; return `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`; };

/**
 * The follow-up. An honest subject (it says what is inside), the document again, the owner's line for this kind of place,
 * a sign-up link that carries the code, one tip from the document, and a footer that says why they are getting it.
 */
export function leadFollowupEmail(i: LeadFollowupInput): LeadFollowupEmail | null {
  const doc = leadDoc(i.file);
  if (!doc) return null;
  const title = leadDocTitle(i.file, i.businessType);
  const noun = businessNoun(i.businessType);
  const guide = doc.kind === "guide";
  const subject = guide ? `Your guide, ${title}, and ${LEAD_DISCOUNT_PERCENT}% off when you're ready` : `Your ${title}, and ${LEAD_DISCOUNT_PERCENT}% off when you're ready`;
  const what = guide ? `the guide "${title}"` : `your ${title}`;
  const tipHead = guide ? "The short version of the guide" : `One tip from the ${title}`;
  const address = i.postalAddress ?? LEAD_POSTAL_ADDRESS;
  const line = `We'll give you ${LEAD_DISCOUNT_PERCENT}% off your first month when you're ready to start your ${noun} with us. Let's handle health compliance as a team.`;
  const price = `Plans start at ${fromPrice()} a month for one location. You get ${TRIAL_DAYS} days free first, then your first month is ${LEAD_DISCOUNT_PERCENT}% less. Your code ${i.code} is already in this link, and it works until ${leadDate(i.codeEndsAt)}.`;
  const why = `You are getting this because you ${guide ? "opened" : "downloaded"} ${guide ? `the guide "${title}"` : `the ${title}`} on cleancleaver.com on ${leadDate(i.consentAt)} and ticked the box to get CleanCleaver food-safety tips and offers.`;
  const text = [
    "Hi,",
    "",
    `Here is ${what} again, so you can find it: ${i.docUrl}`,
    "",
    line,
    "",
    `Start here: ${i.signupUrl}`,
    price,
    "",
    `${tipHead}:`,
    doc.tip,
    "",
    "Zak Kline, CleanCleaver",
    "",
    "--",
    why,
    `Unsubscribe, and we stop at once: ${i.unsubscribeUrl}`,
    address,
  ].join("\n");
  const html = `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escHtml(subject)}</title></head>
<body style="margin:0;padding:0;background:#F4EAD2;color:#2A2118;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:17px;line-height:1.5">
<div style="display:none;max-height:0;overflow:hidden;opacity:0">${escHtml(`${LEAD_DISCOUNT_PERCENT}% off your first month, and ${guide ? `the guide "${title}"` : `your ${title}`} again.`)}</div>
<div style="max-width:560px;margin:0 auto;padding:24px 16px">
<div style="background:#FFFBF1;border:1.5px solid rgba(42,33,24,.2);border-radius:14px;padding:24px">
<p style="margin:0 0 16px">Hi,</p>
<p style="margin:0 0 16px">Here is ${escHtml(what)} again, so you can find it: <a href="${escHtml(i.docUrl)}" style="color:#2A2118;font-weight:700">${guide ? "open the guide" : "open the PDF"}</a>.</p>
<p style="margin:0 0 20px;font-weight:700">${escHtml(line)}</p>
<p style="margin:0 0 12px"><a href="${escHtml(i.signupUrl)}" style="display:inline-block;background:#2A2118;color:#F4EAD2;text-decoration:none;font-weight:700;border-radius:10px;padding:12px 18px">Start with ${TRIAL_DAYS} days free</a></p>
<p style="margin:0 0 20px;font-size:15px">${escHtml(price)}</p>
<p style="margin:0 0 4px;font-weight:700">${escHtml(tipHead)}</p>
<p style="margin:0 0 20px">${escHtml(doc.tip)}</p>
<p style="margin:0">Zak Kline, CleanCleaver</p>
</div>
<p style="margin:16px 4px 0;font-size:13px;color:#5b5044">${escHtml(why)} <a href="${escHtml(i.unsubscribeUrl)}" style="color:#5b5044">Unsubscribe</a> and we stop at once.</p>
<p style="margin:8px 4px 0;font-size:13px;color:#5b5044">${escHtml(address)}</p>
</div></body></html>`;
  return {
    subject, text, html,
    // RFC 8058: a mail app can unsubscribe with one POST to this address; the link in the body goes to the same page.
    headers: [
      { name: "List-Unsubscribe", value: `<${i.unsubscribeUrl}>` },
      { name: "List-Unsubscribe-Post", value: "List-Unsubscribe=One-Click" },
    ],
  };
}
